import axios, { type AxiosError } from "axios";
import toast from "react-hot-toast";
import { API_BASE } from "@/lib/constants";

const client = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 15_000, // 15s timeout — fail fast
});

// ── Request interceptor: attach JWT ───────────────────
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor: auto-refresh + error handling ─
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else if (token) p.resolve(token);
  });
  failedQueue = [];
}

client.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & {
      _retry?: boolean;
    };

    // ── Network error (offline, DNS, CORS) ──────────
    if (!error.response) {
      const isOffline = !navigator.onLine;
      toast.error(
        isOffline
          ? "Нет интернет-соединения"
          : "Сервер недоступен. Попробуйте позже.",
        { id: "network-error" },
      );
      return Promise.reject(error);
    }

    const status = error.response.status;

    // ── 429 Rate Limit ──────────────────────────────
    if (status === 429) {
      const retryAfter = error.response.headers["retry-after"];
      const seconds = retryAfter ? Number(retryAfter) : 30;
      toast.error(`Слишком много запросов. Подождите ${seconds} сек.`, {
        id: "rate-limit",
        duration: seconds * 1000,
      });
      return Promise.reject(error);
    }

    // ── 500+ Server Error ───────────────────────────
    if (status >= 500) {
      toast.error("Ошибка сервера. Мы уже работаем над этим.", {
        id: "server-error",
      });
      return Promise.reject(error);
    }

    // ── 403 Forbidden ───────────────────────────────
    if (status === 403) {
      // Don't show generic toast for auth endpoints — let forms handle their own errors
      const url = originalRequest?.url ?? "";
      if (!url.includes("/auth/")) {
        toast.error("Недостаточно прав для этого действия", {
          id: "forbidden",
        });
      }
      return Promise.reject(error);
    }

    // ── 401 Unauthorized — try refresh ──────────────
    if (status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't retry for login/register endpoints
    if (
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/register") ||
      originalRequest.url?.includes("/auth/refresh")
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return client(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      isRefreshing = false;
      clearAuth();
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post(`${API_BASE}/auth/refresh`, {
        refresh_token: refreshToken,
      });
      const newAccess = data.access_token as string;
      const newRefresh = data.refresh_token as string;

      localStorage.setItem("access_token", newAccess);
      localStorage.setItem("refresh_token", newRefresh);

      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      processQueue(null, newAccess);
      return client(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearAuth();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

/** Clear tokens and redirect to login */
function clearAuth() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  // Only redirect if not already on login page
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
}

/**
 * Extract a human-readable error message from an API error.
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (data && typeof data === "object" && "detail" in data) {
      return String(data.detail);
    }
    if (error.message === "Network Error") {
      return "Нет соединения с сервером";
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return "Произошла ошибка";
}

export default client;
